/**
 * Master list of preset venue categories.
 *
 * - Fixed taxonomy (no free-text). Venues pick 1–3 from this list.
 * - Used by venue Card Settings (multi-select chips, max 3).
 * - Used by the guest map chip filter.
 * - The `display` value is what users see; the `slug` is the canonical
 *   lowercase identifier persisted in the DB.
 */
export const VENUE_CATEGORIES = [
  { slug: 'breakfast-coffee', display: 'Breakfast & Coffee' },
  { slug: 'brunch', display: 'Brunch' },
  { slug: 'street-food', display: 'Street Food' },
  { slug: 'grill', display: 'Grill' },
  { slug: 'fine-dining', display: 'Fine Dining' },
  { slug: 'cocktails-bars', display: 'Cocktails & Bars' },
  { slug: 'wine-bars', display: 'Wine Bars' },
  { slug: 'mediterranean', display: 'Mediterranean' },
  { slug: 'italian', display: 'Italian' },
  { slug: 'indian', display: 'Indian' },
  { slug: 'asian', display: 'Asian' },
  { slug: 'mexican', display: 'Mexican' },
  { slug: 'comfort-food', display: 'Comfort Food' },
  { slug: 'desserts', display: 'Desserts' },
  { slug: 'vegetarian-vegan', display: 'Vegetarian / Vegan' },
];

// Minimum and maximum categories a venue may select. Enforced client-side
// in Card Settings; guests read whatever is stored so a missing venue
// still displays gracefully.
export const VENUE_CATEGORIES_MIN = 1;
export const VENUE_CATEGORIES_MAX = 3;

// Silent migration for legacy slugs and free-typed strings coming out of
// the previous 8-slug taxonomy. When a venue owner opens Card Settings,
// old slugs are remapped to the closest new slug; unknown free-text
// strings are dropped. Keys are lowercase — the normaliser matches
// against both slug form and display form.
const LEGACY_SLUG_MIGRATION = {
  breakfast: 'breakfast-coffee',
  'fancy-food': 'fine-dining',
  seafood: 'mediterranean',
};

export const VENUE_CATEGORY_DISPLAY_BY_SLUG = VENUE_CATEGORIES.reduce((acc, cat) => {
  acc[cat.slug] = cat.display;
  return acc;
}, {});

const VENUE_CATEGORY_SLUG_SET = new Set(VENUE_CATEGORIES.map((c) => c.slug));

/**
 * Convert a stored category value (slug or legacy string) into its
 * user-facing display label. Unknown values are returned as-is so
 * legacy free-text categories still render on the venue's card.
 */
export function displayForCategory(value) {
  if (!value) return '';
  return VENUE_CATEGORY_DISPLAY_BY_SLUG[value] || value;
}

/**
 * Migrate a stored `venue_categories` array to the current taxonomy.
 * - Remaps known legacy slugs (breakfast → breakfast-coffee, etc.)
 * - Drops free-text custom strings that don't match a preset
 * - De-duplicates while preserving first-seen order
 * - Caps the result at VENUE_CATEGORIES_MAX
 */
export function migrateVenueCategories(input) {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  const out = [];
  input.forEach((raw) => {
    if (!raw) return;
    const key = String(raw).trim();
    if (!key) return;
    // Direct match
    if (VENUE_CATEGORY_SLUG_SET.has(key)) {
      if (!seen.has(key)) { seen.add(key); out.push(key); }
      return;
    }
    // Legacy slug remap
    const remapped = LEGACY_SLUG_MIGRATION[key.toLowerCase()];
    if (remapped && VENUE_CATEGORY_SLUG_SET.has(remapped)) {
      if (!seen.has(remapped)) { seen.add(remapped); out.push(remapped); }
    }
    // Otherwise: drop free-typed custom categories silently
  });
  return out.slice(0, VENUE_CATEGORIES_MAX);
}
